import React, { useState } from 'react';
import { X, Trash2, AlertTriangle, User } from 'lucide-react';
import { deleteUser } from '../lib/admin';
import type { UserStats } from '../types/admin';

interface Props {
  user: UserStats;
  onClose: () => void;
  onDelete: () => void;
}

export default function DeleteUserModal({ user, onClose, onDelete }: Props) {
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const expectedConfirmText = 'DELETE USER';
  const isConfirmValid = confirmText === expectedConfirmText;

  const handleDelete = async () => {
    if (!isConfirmValid) {
      setError('Please type DELETE USER to confirm');
      return;
    }

    try {
      setLoading(true);
      setError('');
      
      await deleteUser(user.user_id, reason);
      onDelete();
      onClose();
    } catch (err) {
      console.error('Error deleting user:', err);
      setError('Failed to delete user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="card-pastel p-6 max-w-2xl w-full max-h-[95vh] overflow-y-auto my-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="w-6 h-6 text-red-400" />
            <h3 className="text-lg font-semibold text-gray-800 font-poppins">Delete User Account</h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/60 rounded-full transition-colors text-gray-500 hover:text-gray-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-6">
          {/* Critical Warning */}
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-medium text-red-700 mb-2 font-poppins">⚠️ CRITICAL WARNING: This action is IRREVERSIBLE!</h4>
                <p className="text-red-600 text-sm mb-2 font-poppins">
                  Deleting this user will permanently and completely remove:
                </p>
                <ul className="text-red-600 text-sm space-y-1 list-disc list-inside font-poppins">
                  <li><strong>User account and authentication data</strong></li>
                  <li><strong>ALL {user.registry_count} gift registries</strong> created by this user</li>
                  <li><strong>ALL registry items, claims, and gift data</strong></li>
                  <li><strong>ALL RSVP responses and event data</strong></li>
                  <li><strong>ALL withdrawal requests and financial records</strong></li>
                  <li><strong>ALL notifications and communication history</strong></li>
                  <li><strong>ALL user profile and personal data</strong></li>
                </ul>
                <p className="text-red-700 text-sm mt-2 font-medium font-poppins">
                  💰 Financial Impact: LKR {user.total_collected.toFixed(2)} in collected funds will be affected
                </p>
              </div>
            </div>
          </div>

          {/* User Details */}
          <div className="bg-white/60 p-4 rounded-lg">
            <div className="flex items-center space-x-3 mb-3">
              <User className="w-5 h-5 text-gray-800" />
              <h4 className="font-medium text-gray-800 font-poppins">User to Delete</h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm font-poppins">
              <div>
                <p><span className="text-gray-500">Email:</span></p>
                <p className="text-gray-800 font-medium">{user.email}</p>
              </div>
              <div>
                <p><span className="text-gray-500">Name:</span></p>
                <p className="text-gray-800 font-medium">{user.full_name || 'Not provided'}</p>
              </div>
              <div>
                <p><span className="text-gray-500">Username:</span></p>
                <p className="text-gray-800 font-medium">{user.username || 'Not set'}</p>
              </div>
              <div>
                <p><span className="text-gray-500">Member Since:</span></p>
                <p className="text-gray-800 font-medium">{new Date(user.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <p><span className="text-gray-500">Last Sign In:</span></p>
                <p className="text-gray-800 font-medium">{user.last_sign_in_at ? new Date(user.last_sign_in_at).toLocaleDateString() : 'Never'}</p>
              </div>
            </div>
          </div>

          {/* Financial Summary */}
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h4 className="font-medium text-yellow-700 mb-3 font-poppins">💰 Financial Impact Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm font-poppins">
              <div className="text-center">
                <p className="text-yellow-600">Active Registries</p>
                <p className="text-gray-800 font-bold text-lg">{user.registry_count}</p>
              </div>
              <div className="text-center">
                <p className="text-yellow-600">Total Collected</p>
                <p className="text-gray-800 font-bold text-lg">LKR {user.total_collected.toFixed(2)}</p>
              </div>
              <div className="text-center">
                <p className="text-yellow-600">Total Withdrawn</p>
                <p className="text-gray-800 font-bold text-lg">LKR {user.total_withdrawn.toFixed(2)}</p>
              </div>
              <div className="text-center">
                <p className="text-yellow-600">Pending Requests</p>
                <p className="text-gray-800 font-bold text-lg">{user.pending_requests_count}</p>
              </div>
            </div>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2 font-poppins">
              Reason for User Deletion (Required for audit trail)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-red-500 focus:border-red-500 font-poppins"
              rows={3}
              placeholder="Enter detailed reason for deleting this user account (e.g., policy violation, user request, etc.)"
              required
            />
          </div>

          {/* Confirmation */}
          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2 font-poppins">
              Type <span className="font-bold text-red-400 bg-red-50 px-2 py-1 rounded">DELETE USER</span> to confirm this irreversible action
            </label>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-red-500 focus:border-red-500 font-poppins"
              placeholder="DELETE USER"
            />
          </div>

          {/* Final Warning */}
          <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
            <p className="text-red-700 text-sm text-center font-medium font-poppins">
              ⚠️ This will permanently delete the user and ALL their data. This action CANNOT be undone!
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6 border-t border-white/40">
            <button
              onClick={onClose}
              disabled={loading}
              className="px-6 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50 border border-white/40 rounded-md hover:bg-white/60 transition-colors font-poppins"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={loading || !isConfirmValid || !reason.trim()}
              className="flex items-center justify-center space-x-2 px-6 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors font-poppins"
            >
              <Trash2 className="w-4 h-4" />
              <span>{loading ? 'Deleting User...' : 'Delete User Account'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}